import React, { RefObject, useEffect } from "react";
import JoditEditor from "jodit-react";
import { Check, X } from "lucide-react";
import { defaultEditorConfig } from "../../../../../../constants/configJodit";

interface PageEditorModalProps {
  editorRef: RefObject<any>;
  pageBeingEdited: number | null;
  pageEditorContent: string;
  pageEditorColumns: number;
  handleColumnChange: (columns: number) => void;
  setPageEditorContent: (content: string) => void;
  handleCancelEditPage: () => void;
  handleConfirmEditPage: (content: string) => void; 
  dissertiveMode?: boolean;
}

const editorConfig = {
  ...defaultEditorConfig,
  height: 500,
  toolbarSticky: false,
  buttons: [
    "bold",
    "italic",
    "underline",
    "fontsize",
    "|",
    "ul",
    "ol",
    "|",
    "indent",
    "outdent",
    "|",
    "paragraph",
    "align",
    "|",
    "image",
    "table",
    "link",
  ],
  uploader: { insertImageAsBase64URI: true },
  removeButtons: ["source"],
};

export const PageEditorModal: React.FC<PageEditorModalProps> = ({
  editorRef,
  pageBeingEdited,
  pageEditorContent,
  pageEditorColumns,
  handleColumnChange,
  setPageEditorContent,
  handleCancelEditPage,
  handleConfirmEditPage,
  dissertiveMode,
}) => {
  useEffect(() => {
    if (pageBeingEdited !== null && editorRef.current?.jodit) {
      setTimeout(() => {
        if (editorRef.current?.jodit) {
          editorRef.current.jodit.selection.focus();
        }
      }, 200);
    }
  }, [editorRef, pageEditorContent, pageBeingEdited]);

  if (pageBeingEdited === null) return null;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };


  return (
    <div
      className="modal-backdrop"
      style={{
        position: "fixed",
        inset: "0",
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        zIndex: 9999,
      }}
      onClick={stopPropagation}
    >
      <div
        className="modal-content"
        style={{
          background: "#fff",
          borderRadius: "8px",
          padding: "24px",
          width: "100%",
          maxWidth: "900px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 5px 25px rgba(0,0,0,0.2)",
          position: "relative",
        }}
        onClick={stopPropagation}
      >
        <div className="modal-header mb-3 d-flex justify-content-between align-items-center">
          <h3 className="m-0">Editar Página {pageBeingEdited + 1}</h3>
        </div>

        <div className="mb-3">
          <label htmlFor="numeroColunas" className="form-label fw-bold">
            Número de Colunas
          </label>
          <select
            id="numeroColunas"
            className="form-select"
            value={pageEditorColumns}
            onChange={(e) => handleColumnChange(parseInt(e.target.value))}
            disabled={dissertiveMode} // Desabilita o select no modo de edição
          >
            <option value={1}>1 Coluna</option>
            <option value={2}>2 Colunas</option>
          </select>
        </div>

        <div
          className="modal-body"
          style={{ height: "500px", overflowY: "auto" }}
        >
          <JoditEditor
            ref={editorRef}
            value={pageEditorContent}
            config={editorConfig}
            onBlur={(content) => setPageEditorContent(content)}
          />
        </div>

        <div
          className="modal-footer mt-3 d-flex justify-content-end"
          style={{ gap: "8px" }}
        >
          <button
            onClick={handleCancelEditPage}
            className="btn btn-secondary"
            type="button"
          >
            <X size={18} /> Cancelar
          </button>
          <button
            onClick={() => handleConfirmEditPage(pageEditorContent)}
            className="btn btn-success"
            type="button"
          >
            <Check size={18} /> Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};
